import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaSearch, FaUndo, FaCheckCircle, FaExclamationTriangle, FaUserCheck } from 'react-icons/fa';

const RestoreDeletedStudentsModal = ({ 
  show, 
  onHide, 
  schoolid,
  teacherid,
  teacherName,
  getDeletedStudentsBySchool,
  restoreStudent,
  batchRestoreStudents
}) => {
  const [deletedStudents, setDeletedStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [includeRestored, setIncludeRestored] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all');
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (show && schoolid) {
      loadDeletedStudents();
    }
  }, [show, schoolid, includeRestored]);

  useEffect(() => {
    filterStudents();
  }, [deletedStudents, searchTerm, timeFilter]);

  const loadDeletedStudents = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await getDeletedStudentsBySchool(schoolid, includeRestored, 500, 0);
      setDeletedStudents(response.data || []);
    } catch (error) {
      setMessage({ type: 'danger', text: `Failed to load deleted students: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    let filtered = [...deletedStudents];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(student => 
        student.name?.toLowerCase().includes(search) ||
        student.class_name?.toLowerCase().includes(search) ||
        student.id?.toLowerCase().includes(search)
      );
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch(timeFilter) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setDate(now.getDate() - 30);
          break;
      }
      
      filtered = filtered.filter(student => new Date(student.deleted_at) >= cutoff);
    }

    setFilteredStudents(filtered);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const unrestoredIds = filteredStudents.filter(s => !s.restored_at).map(s => s.id);
      setSelectedStudents(new Set(unrestoredIds));
    } else {
      setSelectedStudents(new Set());
    }
  };

  const handleSelectStudent = (studentId, isRestored) => {
    if (isRestored) return;
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleRestore = async () => {
    if (selectedStudents.size === 0) {
      setMessage({ type: 'warning', text: 'Please select at least one student to restore' });
      return;
    }
    setShowConfirmation(true);
  };

  const confirmRestore = async () => {
    setRestoring(true);
    setMessage({ type: '', text: '' });
    setShowConfirmation(false);

    try {
      const studentIds = Array.from(selectedStudents);
      
      if (studentIds.length === 1) {
        await restoreStudent(studentIds[0], teacherid, teacherName);
        setMessage({ type: 'success', text: '✓ Student restored successfully!' });
      } else {
        const result = await batchRestoreStudents(schoolid, studentIds, teacherid, teacherName);
        setMessage({ type: 'success', text: `✓ Successfully restored ${result.restoredCount} student(s)!` });
        
        if (result.errors && result.errors.length > 0) {
          setMessage(prev => ({
            ...prev,
            text: prev.text + ` ${result.errors.length} failed.`
          }));
        }
      }

      setSelectedStudents(new Set());
      await loadDeletedStudents();
    } catch (error) {
      setMessage({ type: 'danger', text: `Restoration failed: ${error.message}` });
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUserCheck className="me-2" />
            Restore Deleted Students
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {message.text && (
            <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
              {message.text}
            </Alert>
          )}

          <div className="mb-3">
            <div className="row g-2">
              <div className="col-md-6">
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by student name, class, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
              <div className="col-md-3">
                <Form.Select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}>
                  <option value="all">All Time</option>
                  <option value="today">Deleted Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Check
                  type="checkbox"
                  label="Show Already Restored"
                  checked={includeRestored}
                  onChange={(e) => setIncludeRestored(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">
              Showing {filteredStudents.length} of {deletedStudents.length} deleted students
              {selectedStudents.size > 0 && ` | ${selectedStudents.size} selected`}
            </small>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading deleted students...</p>
            </div>
          ) : filteredStudents.length === 0 ? (
            <Alert variant="info">
              <FaCheckCircle className="me-2" />
              No deleted students found. {includeRestored ? 'Try adjusting your filters.' : 'All clean!'}
            </Alert>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedStudents.size > 0 && 
                          selectedStudents.size === filteredStudents.filter(s => !s.restored_at).length}
                      />
                    </th>
                    <th>Student Name</th>
                    <th>Original Class</th>
                    <th>Deleted By</th>
                    <th>Deleted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id} style={{ 
                      opacity: student.restored_at ? 0.6 : 1,
                      backgroundColor: student.restored_at ? '#f8f9fa' : 'white'
                    }}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedStudents.has(student.id)}
                          onChange={() => handleSelectStudent(student.id, student.restored_at)}
                          disabled={!!student.restored_at}
                        />
                      </td>
                      <td>
                        <strong>{student.name || 'Unnamed Student'}</strong>
                        <br />
                        <small className="text-muted">ID: {student.id}</small>
                      </td>
                      <td>
                        {student.class_name || 'Unknown'}
                        <br />
                        <small className="text-muted">{student.class_id}</small>
                      </td>
                      <td>
                        {student.deleted_by_teacher_name || 'Unknown'}
                        <br />
                        <small className="text-muted">{student.deleted_by_teacher_id}</small>
                      </td>
                      <td>
                        {formatDate(student.deleted_at)}
                        <br />
                        <Badge bg="secondary">{getTimeAgo(student.deleted_at)}</Badge>
                      </td>
                      <td>
                        {student.restored_at ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Restored
                          </Badge>
                        ) : (
                          <Badge bg="warning">
                            <FaExclamationTriangle className="me-1" />
                            Deleted
                          </Badge>
                        )}
                        {student.restored_at && (
                          <div className="mt-1">
                            <small className="text-muted">
                              by {student.restored_by_teacher_name}
                              <br />
                              {formatDate(student.restored_at)}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div>
              {selectedStudents.size > 0 && (
                <span className="text-muted">
                  {selectedStudents.size} student(s) selected for restoration
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={onHide} className="me-2">
                Close
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRestore}
                disabled={selectedStudents.size === 0 || restoring}
              >
                {restoring ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <FaUndo className="me-2" />
                    Restore Selected ({selectedStudents.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Restoration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FaExclamationTriangle className="me-2" />
            <strong>Are you sure?</strong>
          </Alert>
          <p>You are about to restore <strong>{selectedStudents.size}</strong> student(s).</p>
          <p className="mb-0">
            <small className="text-muted">
              Restored students will be returned to their original classes.
              Their assessments and data will become active again.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmRestore}>
            <FaUndo className="me-2" />
            Yes, Restore Now
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RestoreDeletedStudentsModal;